import React, {
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useContext,
} from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import {
  CssVarsProvider,
  extendTheme,
  createTheme,
  responsiveFontSizes,
} from '@mui/material/styles';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';

import { selectAppConfigProp } from '../../slices/appState';

export const SCOPE_ID = 'bookingjs-scope';
export const THEME_MODE_STORAGE_KEY = 'bookingjs-theme-mode';

/**
 * Context to expose the Shadow DOM "portal container" and related refs
 * to components that need explicit access (e.g. ScopableDialog).
 */
const DomHandlerContext = React.createContext({
  portalContainer: null, // Element used for MUI portals when constrained
  shadowRoot: null,
  doc: typeof document !== 'undefined' ? document : null,
});

export function useDomHandlerContext() {
  return useContext(DomHandlerContext);
}

/**
 * makeMirroredEmotionCache
 * - Creates an Emotion cache that inserts styles into the shadow root
 *   and mirrors each rule into the light DOM (host document).
 * - This keeps overlays rendered on document.body styled consistently.
 */
function makeMirroredEmotionCache({ key, shadowRoot, doc }) {
  const cacheShadow = createCache({
    key,
    prepend: true,
    container: shadowRoot,
  });

  const cacheBody = createCache({
    key: `${key}-body`,
    prepend: true,
    container: doc.head || doc.body,
  });

  const originalInsert = cacheShadow.insert;
  cacheShadow.insert = function insertion(
    selector,
    serialized,
    sheet,
    shouldCache,
  ) {
    originalInsert.call(cacheShadow, selector, serialized, sheet, shouldCache);
    cacheBody.insert(selector, serialized, cacheBody.sheet, shouldCache);
  };

  return cacheShadow;
}

/**
 * ensureShadowStructure
 * - Creates or reuses the shadowRoot on the host element.
 * - Ensures the internal structure:
 *    - scope-root: wraps both content-root and portal-root
 *    - content-root: where the main app is rendered
 *    - portal-root: where MUI overlays are rendered when constrained
 * - Returns { shadowRoot, scopeRoot, contentRoot, portalRoot }
 */
function ensureShadowStructure(host) {
  const sr = host.shadowRoot || host.attachShadow({ mode: 'open' });
  const mk = () => (host.ownerDocument || document).createElement('div');

  let scopeRoot = sr.querySelector('[data-role="scope-root"]');
  if (!scopeRoot) {
    scopeRoot = mk();
    scopeRoot.setAttribute('data-role', 'scope-root');
    sr.appendChild(scopeRoot);
  }

  let contentRoot = scopeRoot.querySelector('[data-role="content-root"]');
  if (!contentRoot) {
    contentRoot = mk();
    contentRoot.setAttribute('data-role', 'content-root');
    scopeRoot.appendChild(contentRoot);
  }

  let portalRoot = scopeRoot.querySelector('[data-role="portal-root"]');
  if (!portalRoot) {
    portalRoot = mk();
    portalRoot.setAttribute('data-role', 'portal-root');
    scopeRoot.appendChild(portalRoot);
  }

  return { shadowRoot: sr, scopeRoot, contentRoot, portalRoot };
}

/**
 * styleScopeAndPortal
 * - Applies layout/stacking styles to scopeRoot (container) and portalRoot (overlay layer).
 * - scopeRoot gets width/height/overflow and controlling transform when constrained.
 * - portalRoot gets a higher stacking order relative to content.
 */
function styleScopeAndPortal({ scopeRoot, portalRoot, height, constrained }) {
  if (scopeRoot) {
    scopeRoot.style.position = 'relative';
    scopeRoot.style.width = '100%';
    scopeRoot.style.height = height ? String(height) : 'auto';
    scopeRoot.style.overflow = 'hidden';
    scopeRoot.style.transform = constrained ? 'translateZ(0)' : 'none';
  }
  if (portalRoot) {
    portalRoot.style.position = 'relative';
    portalRoot.style.zIndex = '1';
  }
}

/**
 * useShadowDomStructure
 * - Encapsulates the creation of the shadow DOM structure and its styling.
 * - Returns { shadowRoot, contentRoot, portalRoot, doc } once ready; otherwise nulls.
 */
function useShadowDomStructure({ hostRef, height, constrained }) {
  const [shadowRoot, setShadowRoot] = useState(null);
  const [scopeRoot, setScopeRoot] = useState(null);
  const [contentRoot, setContentRoot] = useState(null);
  const [portalRoot, setPortalRoot] = useState(null);

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const nodes = ensureShadowStructure(host);
    setShadowRoot(nodes.shadowRoot);
    setScopeRoot(nodes.scopeRoot);
    setContentRoot(nodes.contentRoot);
    setPortalRoot(nodes.portalRoot);
  }, [hostRef]);

  useLayoutEffect(() => {
    if (!scopeRoot && !portalRoot) return;
    styleScopeAndPortal({ scopeRoot, portalRoot, height, constrained });
  }, [scopeRoot, portalRoot, height, constrained]);

  const doc = (hostRef.current && hostRef.current.ownerDocument) || document;

  return { shadowRoot, contentRoot, portalRoot, doc };
}

/* ---------------------------------------
   Gentle perf improvement #1: cache themes
   ---------------------------------------
   extendTheme is relatively heavy. We cache the themed result per (baseTheme, portalContainer)
   so the same inputs reuse the same theme without recomputing.
*/

// WeakMap<Theme, Map<Element|null, Theme>>
const themeCache = new WeakMap();

/**
 * This utility function takes a custom theme object and ensures it is properly
 * structured for use with Material UI's extendTheme and CssVarsProvider.
 *
 * It supports two scenarios:
 *  1. If the theme already has 'colorSchemes', it returns it as is.
 *  2. If it has a root-level 'palette' (the old modeless style), it moves that
 *     palette into both the 'light' and 'dark' color schemes. */
export function migrateThemeToCssVars(customTheme) {
  if (!customTheme) {
    return {};
  }

  // Check if the theme is already structured for CSS variables (has colorSchemes)
  if (customTheme.colorSchemes) {
    return customTheme;
  }

  // Destructure the palette and the rest of the theme properties
  const { palette, ...rest } = customTheme;

  if (palette) {
    // If a root palette exists (modeless theme), apply it to both light and dark schemes.
    const migratedTheme = {
      ...rest,
      colorSchemes: {
        // Ensure the custom colors are used when the theme is in 'light' mode
        light: { palette },
        // Ensure the custom colors are also used when the theme is in 'dark' mode
        // Note: MUI will automatically generate necessary dark shades for missing keys.
        dark: { palette },
      },
    };

    // Ensure 'palette' is removed from the root to avoid conflicts
    // when passed to extendTheme.
    delete migratedTheme.palette;

    return migratedTheme;
  }

  // If no palette or colorSchemes are found, return the rest of the theme
  return customTheme;
}

/**
 * getThemeWithPortalsCached
 * - Returns a theme with default container set for all portal-based components.
 * - Uses a WeakMap to cache per (baseTheme, portalContainer) so extendTheme runs only when needed.
 */
function getThemeWithPortalsCached(baseTheme = {}, portalContainer) {
  let byContainer = themeCache.get(baseTheme);
  if (!byContainer) {
    byContainer = new Map();
    themeCache.set(baseTheme, byContainer);
  }

  if (byContainer.has(portalContainer)) {
    return byContainer.get(portalContainer);
  }

  const existing = (baseTheme && baseTheme.components) || {};
  const withContainer = (compKey) => ({
    ...existing[compKey],
    defaultProps: {
      ...(existing[compKey] && existing[compKey].defaultProps),
      container: portalContainer || undefined,
    },
  });

  const hasModeSupport = baseTheme.colorSchemes;
  let resultingTheme;

  if (hasModeSupport) {
    // we force light scrollbars usually. Lets remove that here to support modes.

    if (baseTheme?.components?.MuiCssBaseline?.styleOverrides?.body) {
      baseTheme.components.MuiCssBaseline.styleOverrides.body = undefined;
    }

    resultingTheme = migrateThemeToCssVars(baseTheme);
    resultingTheme = extendTheme({
      ...resultingTheme,
      components: {
        ...resultingTheme.components,
        MuiPopover: withContainer('MuiPopover'),
        MuiPopper: withContainer('MuiPopper'),
        MuiDialog: withContainer('MuiDialog'),
        MuiModal: withContainer('MuiModal'),
        MuiSelect: {
          defaultProps: {
            MenuProps: {
              disablePortal: true,
              slotProps: {
                transition: {
                  onEntering: (paper) => {
                    requestAnimationFrame(() => {
                      let el = paper;
                      while (el && el !== document.documentElement) {
                        el = el.parentElement;
                        if (!el) break;
                        const t = getComputedStyle(el).transform;
                        if (t && t !== 'none') {
                          const r = el.getBoundingClientRect();
                          paper.style.top = `${parseFloat(paper.style.top || 0) - r.top}px`;
                          paper.style.left = `${parseFloat(paper.style.left || 0) - r.left}px`;
                          return;
                        }
                      }
                    });
                  },
                },
              },
            },
          },
        },
        MuiMenu: withContainer('MuiMenu'),
        MuiSnackbar: withContainer('MuiSnackbar'),
        MuiTooltip: withContainer('MuiTooltip'),
      },
    });
  }

  if (!hasModeSupport) {
    resultingTheme = createTheme({
      ...baseTheme,
      components: {
        ...baseTheme.components,
        MuiPopover: withContainer('MuiPopover'),
        MuiPopper: withContainer('MuiPopper'),
        MuiDialog: withContainer('MuiDialog'),
        MuiModal: withContainer('MuiModal'),
        MuiSelect: {
          defaultProps: {
            MenuProps: {
              disablePortal: true,
              slotProps: {
                transition: {
                  onEntering: (paper) => {
                    requestAnimationFrame(() => {
                      let el = paper;
                      while (el && el !== document.documentElement) {
                        el = el.parentElement;
                        if (!el) break;
                        const t = getComputedStyle(el).transform;
                        if (t && t !== 'none') {
                          const r = el.getBoundingClientRect();
                          paper.style.top = `${parseFloat(paper.style.top || 0) - r.top}px`;
                          paper.style.left = `${parseFloat(paper.style.left || 0) - r.left}px`;
                          return;
                        }
                      }
                    });
                  },
                },
              },
            },
          },
        },
        MuiMenu: withContainer('MuiMenu'),
        MuiSnackbar: withContainer('MuiSnackbar'),
        MuiTooltip: withContainer('MuiTooltip'),
      },
    });
  }

  resultingTheme = responsiveFontSizes(resultingTheme, { factor: 2.5 });

  byContainer.set(portalContainer, resultingTheme);
  return resultingTheme;
}

/**
 * DomHandler
 * - The main component that ties everything together.
 */
export default function DomHandler({ theme, children }) {
  // Config from store
  const height = useSelector((state) => selectAppConfigProp(state, 'height'));
  const constrainDialogsToContainer = useSelector((state) =>
    selectAppConfigProp(state, 'constrainDialogsToContainer'),
  );
  const constrained = !!constrainDialogsToContainer;

  // Host element that will own the ShadowRoot
  const hostRef = useRef(null);

  // Build Shadow DOM structure and styles
  const { shadowRoot, contentRoot, portalRoot, doc } = useShadowDomStructure({
    hostRef,
    height,
    constrained,
  });

  // Decide where MUI portals go (shadow portal when constrained, otherwise document.body)
  const portalContainer = useMemo(() => {
    if (!portalRoot) return doc.body;
    return constrained ? portalRoot : doc.body;
  }, [constrained, portalRoot, doc]);

  // Base theme from context; derive a version with portal containers set (cached)
  const themeWithPortals = useMemo(
    () => getThemeWithPortalsCached(theme, portalContainer),
    [theme, portalContainer],
  );

  // Emotion cache: write to shadow root and mirror into light DOM for stability
  const emotionCache = useMemo(() => {
    if (!shadowRoot) return null;
    return makeMirroredEmotionCache({
      key: `mui-${SCOPE_ID}`,
      shadowRoot,
      doc,
    });
  }, [shadowRoot, doc]);

  // Always render the host so the ref mounts
  const host = (
    <div
      ref={hostRef}
      // Use a normal block host for predictable layout; avoid display: contents on hosts.
      style={{ display: 'block', width: '100%' }}
    />
  );

  // Shadow not ready yet: render only the host
  if (!shadowRoot || !contentRoot || !emotionCache) {
    return host;
  }

  // The actual app, rendered inside the Shadow DOM
  const appInsideShadow = (
    <CacheProvider value={emotionCache}>
      {/* Inject CSS variables into the light DOM for portals to document.body */}
      <CssVarsProvider
        defaultMode="light"
        modeStorageKey={THEME_MODE_STORAGE_KEY}
        theme={themeWithPortals}
        documentNode={doc}
        colorSchemeNode={doc.documentElement}
      >
        {/* Inject the same CSS variables into the Shadow DOM */}
        <CssVarsProvider
          defaultMode="light"
          modeStorageKey={THEME_MODE_STORAGE_KEY}
          theme={themeWithPortals}
          documentNode={shadowRoot}
          colorSchemeNode={shadowRoot}
        >
          <DomHandlerContext.Provider
            value={{ portalContainer, shadowRoot, doc }}
          >
            <Box
              id={SCOPE_ID}
              sx={{
                position: 'relative',
                overflow: 'hidden',
                width: '100%',
                // Container queries: provide a name to target with theme.containerQueries(SCOPE_ID)
                containerType: 'inline-size',
                containerName: SCOPE_ID,
                // Explicit font reset — cuts CSS inheritance from the host
                // page. Without this, font-family/size/weight/etc. inherit
                // through the shadow DOM boundary and override MUI's
                // theme-based values for any element that doesn't have an
                // explicit Typography variant.
                fontFamily: (theme) => theme.typography.fontFamily,
                fontSize: (theme) => theme.typography.fontSize,
                fontWeight: 400,
                lineHeight: 1.5,
                letterSpacing: 'normal',
                textTransform: 'none',
                fontStyle: 'normal',
                wordSpacing: 'normal',
                color: (theme) => theme.palette.text.primary,
              }}
            >
              {children}
            </Box>
          </DomHandlerContext.Provider>
        </CssVarsProvider>
      </CssVarsProvider>
    </CacheProvider>
  );

  return (
    <>
      {host}
      {createPortal(appInsideShadow, contentRoot)}
    </>
  );
}
